// Your profile (PRD §3.12). Opened by tapping your name/icon in the header.
// You can change your emoji or upload a profile pic (→ R2) — and that's it.
// Username/password aren't self-editable: if you forget them, Messy resets them
// and re-sends via WhatsApp (decision 2026-07-01). Also holds the Log out action.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../auth/AuthContext";
import { api, ApiError } from "../lib/api";
import { EMOJIS } from "../lib/emojis";
import { Avatar } from "./Avatar";

export function ProfileSheet({ onClose }: { onClose: () => void }) {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  if (!user) return null;

  async function pickEmoji(emoji: string) {
    if (!user || busy || emoji === user.emoji) return;
    setError(null);
    const prev = user;
    updateUser({ ...user, emoji }); // optimistic
    try {
      const { user: saved } = await api.updateProfile({ emoji });
      updateUser(saved);
    } catch (err) {
      updateUser(prev);
      setError(err instanceof ApiError ? err.message : "Couldn't save that — try again.");
    }
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file later
    if (!file) return;
    setError(null);
    setUploadPct(0);
    setBusy(true);
    try {
      const { user: saved } = await api.uploadAvatar(file, setUploadPct);
      updateUser(saved);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't upload that photo — try again.");
    } finally {
      setUploadPct(null);
      setBusy(false);
    }
  }

  async function removePhoto() {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      const { user: saved } = await api.updateProfile({ avatarUrl: null });
      updateUser(saved);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't remove that — try again.");
    } finally {
      setBusy(false);
    }
  }

  async function onLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-6 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <motion.div
        initial={{ y: 12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="w-full max-w-md overflow-hidden rounded-2xl border-2 border-ink/10 bg-paper shadow-[0_8px_32px_rgba(0,0,0,0.18)]"
      >
        <div className="flex items-center justify-between border-b-2 border-ink/10 px-5 py-4">
          <h2 className="text-lg font-bold text-ink">Your profile</h2>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-ink-muted hover:bg-paper-deep hover:text-ink"
          >
            ×
          </button>
        </div>

        <div className="max-h-[80vh] overflow-y-auto px-5 py-5">
          {/* Who you are */}
          <div className="flex items-center gap-3">
            <Avatar person={user} size={64} />
            <div className="min-w-0">
              <p className="truncate font-display text-2xl text-ink">{user.handle}</p>
              <p className="truncate text-sm text-ink-muted">{user.realName}</p>
            </div>
          </div>

          {error ? (
            <p className="mt-4 rounded-lg border-2 border-love/30 bg-love/5 px-3 py-2 text-sm text-love">
              {error}
            </p>
          ) : null}

          {/* Profile photo */}
          <div className="mt-5">
            <span className="mb-2 block text-sm font-semibold text-ink-soft">Profile photo</span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-md border-2 border-ink bg-paper px-4 py-2 text-sm font-semibold text-citrus-deep shadow-[2px_2px_0_0_var(--color-ink)] transition-transform active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-60"
              >
                {uploadPct !== null
                  ? `Uploading ${uploadPct}%`
                  : user.avatarUrl
                    ? "Change photo 📷"
                    : "Upload photo 📷"}
              </button>
              {user.avatarUrl ? (
                <button
                  type="button"
                  onClick={() => void removePhoto()}
                  disabled={busy}
                  className="text-sm font-semibold text-love/70 hover:text-love disabled:opacity-50"
                >
                  Remove
                </button>
              ) : null}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
            <p className="mt-2 text-xs text-ink-muted">
              A photo replaces your emoji everywhere your name shows.
            </p>
          </div>

          {/* Emoji */}
          <div className="mt-5">
            <span className="mb-2 block text-sm font-semibold text-ink-soft">Or pick an emoji</span>
            <div className="flex flex-wrap gap-1.5">
              {EMOJIS.map((e) => {
                const active = !user.avatarUrl && user.emoji === e;
                return (
                  <button
                    key={e}
                    type="button"
                    onClick={() => void pickEmoji(e)}
                    disabled={busy}
                    aria-pressed={active}
                    className={
                      "flex h-9 w-9 items-center justify-center rounded-md border-2 text-lg disabled:opacity-50 " +
                      (active ? "border-ink bg-paper-deep" : "border-transparent hover:border-ink/20")
                    }
                  >
                    {e}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Credentials note */}
          <p className="mt-5 rounded-lg border-2 border-ink/10 bg-paper-deep px-3 py-2 text-xs text-ink-muted">
            Forgot your username or password? Message Messy — he'll sort you out. 🍊
          </p>

          {/* Log out */}
          <div className="mt-5 border-t-2 border-ink/10 pt-4">
            <button
              onClick={() => void onLogout()}
              className="w-full rounded-xl border-2 border-ink/20 px-4 py-2.5 text-sm font-semibold text-ink-soft hover:border-ink hover:text-ink"
            >
              Log out
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
